import { format, Locale } from 'date-fns'

import {
  SpamReasonMapping,
  SpamReportCategory,
  SpamSourceMapping,
} from '@app/api/spamDetection'

import { CheckStatus, SpamCheckEvent, SpamCheckMode } from '@app/models'
import { getLocalDate } from '@app/utils'

export type DetectionReason = {
  source: string
  reason: string
  status: string
  category: SpamReportCategory
}

export type HistoryItem = {
  event: SpamCheckEvent
  reasons: DetectionReason[]
}

export const extractSpamInfo = (
  event: SpamCheckEvent
): { reasons: DetectionReason[] } => {
  const reasons: DetectionReason[] = []

  for (const [source, details] of Object.entries(event.result.details)) {
    for (const detail of details) {
      if (
        detail.mode === SpamCheckMode.Detection ||
        detail.status == CheckStatus.None
      ) {
        continue
      }

      const category =
        detail.status == CheckStatus.Spam
          ? SpamReasonMapping[detail.reason] ?? 'Spam'
          : 'Safe'
      const sourceMapped = SpamSourceMapping[source] ?? source
      reasons.push({
        source: sourceMapped,
        reason:
          sourceMapped == 'Manual'
            ? detail.spamlistEntries
              ?.map((e) => e.createdByUserName)
              .join(', ') ?? ''
            : detail.reason,
        status: detail.status,
        category,
      })
    }
  }
  return { reasons }
}

export const getCategoryClass = (
  category: SpamReportCategory | null,
  source: string,
  styles: Record<string, string>
): string => {
  if (!category) return ''

  const isManual = source === 'Manual'

  const map: Record<SpamReportCategory, string> = {
    Spam: styles.spam,
    Phishing: isManual ? styles.spam : styles.phishing,
    IllegalContent: isManual ? styles.spam : styles.illegal,
    Safe: styles.safe,
  }

  return map[category] ?? ''
}

export const unwrapToHistoryItems = (
  events: SpamCheckEvent[]
): HistoryItem[] => {
  return (
    events
      ?.map((event) => {
        const { reasons } = extractSpamInfo(event)
        return { event, reasons }
      })
      .sort(
        (a, b) =>
          new Date(b.event.creationDate).getTime() -
          new Date(a.event.creationDate).getTime()
      ) || []
  )
}

export const formatDate = (creationDate: string, locale: Locale) =>
  format(getLocalDate(creationDate), 'Pp', {
    locale,
  })
